/**
 * NodePickerPopover - Lista de blocos disponíveis em um popover
 *
 * Usada tanto pelo botão "+" das linhas do mapa quanto pelo "+" abaixo de um
 * bloco sem saída. Reaproveita exatamente o mesmo catálogo da paleta lateral
 * (`WORKFLOW_PALETTE`), garantindo que todos os pontos de inserção ofereçam as
 * mesmas opções. Camada de apresentação apenas.
 *
 * @version 1.2.0
 * RELEASE: 2026-08-31 v1.2.0 - Correção: remoção dos handlers de captura que
 *          impediam a abertura do popover do botão "+".
 * @version 1.1.0
 * RELEASE: 2026-08-31 v1.1.0 - Rolagem interna com altura fixa e selo colorido
 *          por tipo de bloco (mesmos tokens do mapa).
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (inserção guiada de blocos)
 */

import { useMemo, useState, type ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { WORKFLOW_PALETTE, GRUPOS_DA_PALETA, type WorkflowPaletteItem } from '../workflowNodeMeta';
import { ESTILO_DA_FORMA, formaDoNode } from './bpmnShapes';

interface NodePickerPopoverProps {
  /** Elemento que abre o popover (botão "+") */
  trigger: ReactNode;
  disabled?: boolean;
  onSelect: (item: WorkflowPaletteItem) => void;
}

export function NodePickerPopover({ trigger, disabled, onSelect }: NodePickerPopoverProps) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');

  // Filtro simples por rótulo/descrição, sem acentuação sensível
  const filtrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return WORKFLOW_PALETTE;
    return WORKFLOW_PALETTE.filter(
      (i) => i.label.toLowerCase().includes(termo) || i.description.toLowerCase().includes(termo),
    );
  }, [busca]);

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      {/* O bloqueio do clique fica no próprio botão (fase de bolha); na fase de
          captura o Radix não chegava a receber o evento e o popover não abria. */}
      <PopoverTrigger asChild disabled={disabled}>
        {trigger}
      </PopoverTrigger>
      <PopoverContent align="center" className="w-[300px] p-0" onClick={(e) => e.stopPropagation()}>
        <div className="border-b p-2">
          <Input
            autoFocus
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar bloco..."
            className="h-8"
          />
        </div>

        <ScrollArea className="h-[320px]">
          <div className="space-y-3 p-2">
            {GRUPOS_DA_PALETA.map((grupo) => {
              const itens = filtrados.filter((i) => i.grupo === grupo);
              if (itens.length === 0) return null;

              return (
                <div key={grupo} className="space-y-1">
                  <p className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {grupo}
                  </p>

                  {itens.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => {
                          setAberto(false);
                          setBusca('');
                          onSelect(item);
                        }}
                        className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-muted"
                      >
                        <span
                          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
                            ESTILO_DA_FORMA[formaDoNode(item.nodeType, item.defaultConfig)].selo
                          }`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground">{item.label}</span>
                          <span className="block text-[11px] text-muted-foreground">{item.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}

            {filtrados.length === 0 && (
              <p className="px-1 py-4 text-center text-xs text-muted-foreground">Nenhum bloco encontrado.</p>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
